import React from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '@/components/shared/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import SearchableSelect from '@/components/shared/SearchableSelect';
import { accountingApi } from '@/lib/api/accounting';
import {
  readChequeBooks,
  writeChequeBooks,
  type ChequeBookRow,
} from '@/lib/chequeBooksStorage';
import { getChequeVisualSettings } from '@/lib/chequeVisualSettings';

function toFaDigits(value: string | number): string {
  return String(value).replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)]);
}

function ChequeStampOverlay() {
  const v = getChequeVisualSettings();
  if (!v.sealDataUrl && !v.signatureDataUrl) return null;
  return (
    <div className="pointer-events-none absolute bottom-2 end-2 flex max-w-[48%] items-end justify-end gap-1">
      {v.sealDataUrl ? <img src={v.sealDataUrl} alt="" className="h-9 w-9 object-contain drop-shadow-sm sm:h-10 sm:w-10" /> : null}
      {v.signatureDataUrl ? (
        <img src={v.signatureDataUrl} alt="" className="h-8 max-w-[4.5rem] object-contain drop-shadow-sm sm:h-9" />
      ) : null}
    </div>
  );
}

const ChequeBooksPage: React.FC = () => {
  const [bankAccountId, setBankAccountId] = React.useState('');
  const [serie, setSerie] = React.useState('');
  const [fromLeaf, setFromLeaf] = React.useState('');
  const [leafCountInput, setLeafCountInput] = React.useState('');
  const [branchName, setBranchName] = React.useState('');
  const [rows, setRows] = React.useState<ChequeBookRow[]>(() => readChequeBooks());
  const [bankOptions, setBankOptions] = React.useState<Array<{ value: string; label: string }>>([]);
  const [error, setError] = React.useState('');
  const [message, setMessage] = React.useState('');
  const from = Number(fromLeaf || 0);
  const leafCount = Number(leafCountInput || 0);
  const to = from > 0 && leafCount > 0 ? from + leafCount - 1 : 0;

  React.useEffect(() => {
    accountingApi
      .bankAccounts()
      .then((list) => {
        setBankOptions(
          list.map((b) => ({
            value: String(b.id),
            label: `${b.bank_name} - ${b.name}`,
          })),
        );
      })
      .catch(() => setBankOptions([]));
  }, []);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');
    const fromN = Number(fromLeaf);
    const count = Number(leafCountInput);
    const toN = fromN > 0 && count > 0 ? fromN + count - 1 : 0;
    if (!bankAccountId || !serie.trim() || !fromLeaf.trim() || !leafCountInput.trim()) {
      setError('حساب بانکی، سری، از شماره برگ و تعداد برگ الزامی است.');
      return;
    }
    if (!Number.isFinite(fromN) || !Number.isFinite(count) || fromN <= 0 || count <= 0) {
      setError('بازه شماره برگ معتبر نیست.');
      return;
    }
    const label = bankOptions.find((x) => x.value === bankAccountId)?.label ?? bankAccountId;
    const next: ChequeBookRow[] = [
      {
        id: `cb-${Date.now()}`,
        bankAccountId,
        bankAccountLabel: label,
        serie: serie.trim(),
        fromLeaf: String(fromN),
        toLeaf: String(toN),
        branchName: branchName.trim(),
        createdAt: new Date().toISOString(),
      },
      ...rows,
    ];
    setRows(next);
    writeChequeBooks(next);
    setMessage(`دسته‌چک با موفقیت ثبت شد. تعداد ${toFaDigits(toN - fromN + 1)} برگ تعریف شد.`);
    setSerie('');
    setFromLeaf('');
    setLeafCountInput('');
    setBranchName('');
  };

  return (
    <div className="w-full min-w-0 space-y-6">
      <PageHeader
        title="تعریف دسته‌چک"
        description="تعریف دسته‌ای دسته‌چک به‌صورت بازه برگه؛ ثبت اطلاعات روی هر برگه در صفحهٔ «ثبت برگه چک» انجام می‌شود."
        actions={
          <Button asChild variant="secondary">
            <Link to="/cheque-books/register">ثبت برگه چک</Link>
          </Button>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">ثبت دسته‌چک جدید</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="grid grid-cols-1 gap-4 lg:grid-cols-12">
            {error ? (
              <div className="lg:col-span-12 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                {error}
              </div>
            ) : null}
            {message ? (
              <div className="lg:col-span-12 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
                {message}
              </div>
            ) : null}
            <div className="space-y-3 lg:col-span-7">
              <div>
                <label className="mb-1 block text-xs text-slate-600">حساب بانکی</label>
                <SearchableSelect
                  value={bankAccountId}
                  onChange={setBankAccountId}
                  options={bankOptions}
                  placeholder="انتخاب حساب بانکی"
                />
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-xs text-slate-600">سری</label>
                  <Input value={serie} onChange={(e) => setSerie(e.target.value)} placeholder="مثلاً A1" />
                </div>
                <div>
                  <label className="mb-1 block text-xs text-slate-600">شعبه (اختیاری)</label>
                  <Input value={branchName} onChange={(e) => setBranchName(e.target.value)} />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <div>
                  <label className="mb-1 block text-xs text-slate-600">از شماره برگ</label>
                  <Input dir="ltr" value={fromLeaf} onChange={(e) => setFromLeaf(e.target.value.replace(/\D/g, ''))} />
                </div>
                <div>
                  <label className="mb-1 block text-xs text-slate-600">تعداد برگ</label>
                  <Input
                    dir="ltr"
                    value={leafCountInput}
                    onChange={(e) => setLeafCountInput(e.target.value.replace(/\D/g, ''))}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs text-slate-600">تا شماره برگ (خودکار)</label>
                  <Input dir="ltr" value={to > 0 ? String(to) : ''} readOnly />
                </div>
              </div>
              <div className="flex justify-end">
                <Button type="submit">ثبت دسته‌چک</Button>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="relative overflow-hidden rounded-xl border border-violet-200 bg-gradient-to-r from-violet-50 via-rose-50 to-violet-100 p-4 shadow-sm">
                <ChequeStampOverlay />
                <div className="mb-3 flex items-start justify-between">
                  <div>
                    <div className="text-xs text-slate-500">بانک</div>
                    <div className="text-sm font-semibold text-slate-800">
                      {bankOptions.find((x) => x.value === bankAccountId)?.label || '---'}
                    </div>
                  </div>
                  <div className="rounded-md bg-white/70 px-2 py-1 text-xs font-mono text-slate-700">
                    سری {serie || '---'}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs text-slate-600">
                  <div className="rounded-md border border-white/70 bg-white/60 p-2">
                    از برگ: <span className="font-mono text-slate-800">{fromLeaf || '---'}</span>
                  </div>
                  <div className="rounded-md border border-white/70 bg-white/60 p-2">
                    تا برگ: <span className="font-mono text-slate-800">{to > 0 ? String(to) : '---'}</span>
                  </div>
                  <div className="col-span-2 rounded-md border border-white/70 bg-white/60 p-2">
                    شعبه: <span className="text-slate-800">{branchName || '---'}</span>
                  </div>
                </div>
                <div className="mt-3 text-center text-xs text-violet-700">
                  پیش‌نمایش ظاهری دسته‌چک (حس فرم واقعی)
                </div>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">لیست دسته‌چک‌های تعریف‌شده</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
            {rows.map((r) => {
              const count = Number(r.toLeaf) >= Number(r.fromLeaf) ? Number(r.toLeaf) - Number(r.fromLeaf) + 1 : 0;
              return (
                <div
                  key={r.id}
                  className="relative overflow-hidden rounded-xl border border-indigo-200 bg-gradient-to-r from-indigo-50 via-violet-50 to-rose-50 p-4 shadow-sm"
                >
                  <ChequeStampOverlay />
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <div className="min-w-0 text-sm font-semibold text-slate-800">{r.bankAccountLabel}</div>
                    <div className="shrink-0 rounded-md bg-white/70 px-2 py-1 text-xs font-mono">سری {r.serie}</div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div className="rounded-md border bg-white/70 p-2">
                      از: <span className="font-mono">{r.fromLeaf}</span>
                    </div>
                    <div className="rounded-md border bg-white/70 p-2">
                      تا: <span className="font-mono">{r.toLeaf}</span>
                    </div>
                    <div className="rounded-md border bg-white/70 p-2">
                      تعداد: <span className="font-mono">{toFaDigits(count)}</span>
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-600">
                    <span>شعبه: {r.branchName || '-'}</span>
                    <Button asChild size="sm" variant="outline" className="h-7 text-xs">
                      <Link to={`/cheque-books/register?book=${encodeURIComponent(r.id)}`}>ثبت برگه از این دسته</Link>
                    </Button>
                  </div>
                </div>
              );
            })}
            {rows.length === 0 ? (
              <div className="rounded-md border border-dashed px-3 py-6 text-center text-sm text-slate-500">
                هنوز دسته‌چکی تعریف نشده است. ابتدا دسته را اینجا تعریف کنید، سپس از صفحهٔ «ثبت برگه چک» برگ را انتخاب کنید.
              </div>
            ) : null}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ChequeBooksPage;
